/** @module The signed-in session: access token in memory, refresh token in a cookie. */
import { api, ApiError, problemFrom } from "@/api/client";
import { readString, removeKey, writeString } from "@/lib/storage";

const REMEMBER_KEY = "pokescan.remember";

export interface Account {
  id: string;
  email: string;
  display_name: string;
  role: string;
  locale: string;
}

interface SessionState {
  token: string | null;
  account: Account | null;
  status: "unknown" | "signed-in" | "signed-out";
}

let state: SessionState = { token: null, account: null, status: "unknown" };
const listeners = new Set<() => void>();

function publish(next: SessionState): void {
  state = next;
  for (const listener of listeners) listener();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getSession(): SessionState {
  return state;
}

/** The token is deliberately not stored: a reload asks the refresh cookie instead. */
export function accessToken(): string | null {
  return state.token;
}

export function staysSignedIn(): boolean {
  return readString(REMEMBER_KEY) === "1";
}

async function loadAccount(token: string): Promise<Account> {
  const { data, response } = await api.GET("/api/v1/me", {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!data) {
    throw problemFrom(response);
  }
  return data;
}

export async function signIn(email: string, password: string, remember: boolean): Promise<Account> {
  // The app shell comes from the cache even without a network, so a failed sign-in has
  // to say which of the two it was: wrong password, or no way to reach the server.
  const attempt = await api
    .POST("/api/v1/auth/login", { body: { email, password } })
    .catch(() => null);
  if (attempt === null) {
    throw new ApiError(0, "offline", "The server cannot be reached.");
  }
  const { data, response } = attempt;
  if (!data) {
    throw problemFrom(response);
  }
  const token = data.access_token;
  const account = await loadAccount(token);
  if (remember) {
    writeString(REMEMBER_KEY, "1");
  } else {
    removeKey(REMEMBER_KEY);
  }
  publish({ token, account, status: "signed-in" });
  return account;
}

let refreshing: Promise<string | null> | null = null;

/**
 * Ask the refresh cookie for a new access token; used on start and after a 401.
 *
 * Several requests expire at once (a review page loads a dozen pictures). Each asking on its
 * own would replay the same refresh token, and a replayed token revokes the whole family:
 * so everybody waits for the one request in flight.
 */
export function refresh(): Promise<string | null> {
  refreshing ??= refreshOnce().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

async function refreshOnce(): Promise<string | null> {
  const { data } = await api.POST("/api/v1/auth/refresh", {});
  if (!data) {
    publish({ token: null, account: null, status: "signed-out" });
    return null;
  }
  const token = data.access_token;
  try {
    const account = state.account ?? (await loadAccount(token));
    publish({ token, account, status: "signed-in" });
  } catch {
    publish({ token, account: null, status: "signed-in" });
  }
  return token;
}

export async function restore(): Promise<void> {
  if (!staysSignedIn()) {
    publish({ token: null, account: null, status: "signed-out" });
    return;
  }
  await refresh();
}

export async function signOut(): Promise<void> {
  try {
    await api.POST("/api/v1/auth/logout", {});
  } catch {
    // Even if the server cannot be reached, this browser is signed out.
  }
  removeKey(REMEMBER_KEY);
  publish({ token: null, account: null, status: "signed-out" });
}

/** Test seam: put the module back into a known state. */
export function resetSession(): void {
  publish({ token: null, account: null, status: "unknown" });
}
